import React, { useId } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormProvider from "../../../components/FormProviders";
import TextInputField from "../../../components/FormField/TextInputField";
import SoftBox from "../../../components/SoftBox";
import SoftButton from "../../../components/SoftButton";
import SoftTypography from "../../../components/SoftTypography";
import { useDisclosure } from "../../../hooks/useDisclosure";
import { useAddBrandNotification } from "../../settings/hooks";
import { defaultValues, schema } from "../schemas/webhook";

/** Adds or edits the selected brand's default webhook URL. */
const WebhookFormModal = ({ brand }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate, isLoading } = useAddBrandNotification();
  const titleId = useId();
  const methods = useForm({ defaultValues, resolver: yupResolver(schema) });
  const { reset, handleSubmit } = methods;
  const isEditing = Boolean(brand.notificationUrl);

  const handleOpen = () => {
    reset({ notificationUrl: brand.notificationUrl || "" });
    onOpen();
  };

  const onSubmit = ({ notificationUrl }) => {
    mutate({ brandId: brand._id, notificationUrl }, { onSuccess: onClose });
  };

  return (
    <>
      <SoftButton
        variant={isEditing ? "outlined" : "contained"}
        color={isEditing ? "secondary" : "info"}
        size={isEditing ? "small" : "medium"}
        onClick={handleOpen}
        disabled={isLoading}
      >
        {isEditing ? "Edit" : "Add webhook"}
      </SoftButton>
      <Dialog
        open={isOpen}
        onClose={isLoading ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby={titleId}
      >
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle id={titleId}>{isEditing ? "Edit webhook" : "Add webhook"}</DialogTitle>
          <DialogContent>
            <SoftBox pt={2}>
              <SoftTypography
                component="p"
                variant="caption"
                color="text"
                sx={{ mb: 2, overflowWrap: "anywhere" }}
              >
                {brand.name}
              </SoftTypography>
              <TextInputField
                name="notificationUrl"
                label="Webhook URL"
                placeholder="https://your-domain.com/webhooks/payments"
                inputProps={{ "aria-label": "Webhook URL", inputMode: "url", readOnly: isLoading }}
                autoFocus
              />
            </SoftBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <SoftButton variant="outlined" onClick={onClose} disabled={isLoading}>
              Cancel
            </SoftButton>
            <SoftButton color="info" type="submit" disabled={isLoading} isLoading={isLoading}>
              Save webhook
            </SoftButton>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
};

WebhookFormModal.propTypes = {
  brand: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    notificationUrl: PropTypes.string,
  }).isRequired,
};

export default WebhookFormModal;
